import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../css/AddUnit.css';

const AddProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [productName, setProductName] = useState('');
  const [unitId, setUnitId] = useState('');
  const [units, setUnits] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingUnits, setIsLoadingUnits] = useState(true);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [productId, setProductId] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch units for the dropdown
  useEffect(() => {
    const fetchUnits = async () => {
      setIsLoadingUnits(true);
      try {
        const response = await fetch(`${API_URL}add-unit-list.php`);
        const data = await response.json();
        if (data.units) {
          setUnits(data.units);
        } else {
          setUnits([]);
        }
      } catch (error) {
        console.error('Error fetching units:', error);
        showToast('Error loading units. Please try again.', 'error');
        setUnits([]);
      } finally {
        setIsLoadingUnits(false);
      }
    };
    fetchUnits();
  }, [API_URL]);

  useEffect(() => {
    // Check if we're in edit mode
    if (location.state && location.state.product && location.state.isEditing) {
      const { product } = location.state;
      setProductId(product.id);
      setIsEditing(true);
      // Fetch product details from API
      fetchProductDetails(product.id);
    } else {
      setProductName('');
      setUnitId('');
      setProductId(null);
      setIsEditing(false);
    }
  }, [location.state]);

  const fetchProductDetails = async (productId) => {
    setIsLoadingProduct(true);
    try {
      const response = await fetch(`${API_URL}get-product.php?id=${productId}`);
      const data = await response.json();
      console.log('Product API Response:', data); // Debug log
      
      if (data.status && data.data) {
        const product = data.data;
        console.log('Setting product data:', { name: product.product, unitId: product.unitname_id }); // Debug log
        setProductName(product.product || '');
        setUnitId(product.unitname_id || '');
      } else {
        showToast(data.message || 'Failed to fetch product details.', 'error');
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
      showToast('Error loading product details. Please try again.', 'error');
    } finally {
      setIsLoadingProduct(false);
    }
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      setProductName('');
      setUnitId('');
      setProductId(null);
      setIsEditing(false);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productName.trim()) {
      showToast('Please enter a product name', 'error');
      return;
    }
    if (!unitId) {
      showToast('Please select a unit', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      if (isEditing) {
        // Edit mode: send JSON to edit-product.php
        const body = {
          product_id: productId,
          product: productName.trim(),
          unitname_id: unitId,
        };
        const response = await fetch(`${API_URL}edit-product.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const result = await response.json();
        if ((result.message && result.message.includes('successfully')) || result.status === 'success') {
          showToast(`Product "${productName}" updated successfully!`, 'success');
          setTimeout(() => {
            navigate('/product-list');
          }, 1500);
        } else {
          showToast(result.message || 'Error updating product. Please try again.', 'error');
        }
      } else {
        // Add mode: send FormData to add-product.php
        const formData = new FormData();
        formData.append('product', productName.trim());
        formData.append('unitname_id', unitId);
        const response = await fetch(`${API_URL}add-product.php`, {
          method: 'POST',
          body: formData,
        });
        if (response.ok) {
          const result = await response.json();
          if (result.status) {
            showToast(`Product "${productName}" added successfully!`, 'success');
            setProductName('');
            setUnitId('');
            // setTimeout(() => {
            //   navigate('/product-list');
            // }, 1500);
          } else {
            showToast(result.message || 'Error adding product. Please try again.', 'error');
          }
        } else {
          showToast('Network error. Please try again.', 'error');
        }
      }
    } catch (error) {
      showToast('Error saving product. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-category-content">
      <div className="page-header" style={{display: 'block'}}>
        <h1>{isEditing ? 'Edit Product' : 'Add Product'}</h1>
        <p>{isEditing ? 'Update product information' : 'Create a new product for your inventory'}</p>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="add-unit-form">
          <div className="form-group">
            <label htmlFor="productName">Product Name</label>
            <input
              type="text"
              id="productName"
              name="productName"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="Enter product name (e.g., Rice, Sugar, Oil)"
              required
              disabled={isSubmitting || isLoadingProduct}
            />
            {isLoadingProduct && <div className="loading-text">Loading product details...</div>}
          </div>

          <div className="form-group">
            <label htmlFor="unitId">Unit</label>
            <select
              id="unitId"
              name="unitId"
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              required
              disabled={isSubmitting || isLoadingUnits || isLoadingProduct}
            >
              <option value="">Select a unit</option>
              {units.map(unit => (
                <option key={unit.id} value={unit.id}>
                  {unit.unitname}
                </option>
              ))}
            </select>
            {isLoadingUnits && <div className="loading-text">Loading units...</div>}
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting || isLoadingUnits || isLoadingProduct}
          >
            {isSubmitting 
              ? (isEditing ? 'Updating Product...' : 'Adding Product...')
              : (isEditing ? 'Update Product' : 'Add Product')}
          </button>
        </form>
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close" 
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProduct; 